<template>
    <div
        v-if="hasTooltipContentSlot"
        ref="trigger"
        :class="['tooltip-trigger', { 'tooltip-trigger--full': fullWidth }]"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
        @focusin="handleFocusIn"
        @focusout="handleFocusOut"
        @click="handleActivation"
        @keydown.esc="handleEscape"
    >
        <slot name="tooltip-trigger"></slot>

        <Teleport to="body">
            <div
                :id="tooltipId"
                ref="tooltip"
                role="tooltip"
                :aria-hidden="!isVisible"
                :data-side="resolvedSide"
                :class="[
                    'tooltip-text',
                    {
                        'tooltip-text--visible': isVisible && isPositioned,
                    },
                ]"
                :style="floatingStyle"
            >
                <slot name="tooltip-content"></slot>
                <span
                    ref="arrow"
                    class="tooltip-arrow"
                    :style="arrowStyle"
                    aria-hidden="true"
                ></span>
            </div>
        </Teleport>
    </div>

    <slot v-else name="tooltip-trigger"></slot>
</template>

<script>
import {
    arrow,
    autoUpdate,
    computePosition,
    flip,
    offset as floatingOffset,
    shift,
} from '@floating-ui/dom';

const VIEWPORT_PADDING = 8;
const ARROW_PADDING = 8;
const ARROW_SIZE = 8;
const DEFAULT_MAX_WIDTH_REM = 18;
const DEFAULT_TOOLTIP_OFFSET = 8;
const FOCUSABLE_SELECTOR = [
    'button',
    'a[href]',
    'input:not([type="hidden"])',
    'select',
    'textarea',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

let tooltipCounter = 0;

export default {
    name: 'ToolTip',
    props: {
        fullWidth: {
            type: Boolean,
            default: false,
        },
        position: {
            type: String,
            default: 'auto',
            validator: (value) => ['auto', 'top', 'bottom'].includes(value),
        },
        align: {
            type: String,
            default: 'center',
            validator: (value) => ['start', 'center', 'end'].includes(value),
        },
        offset: {
            type: Number,
            default: DEFAULT_TOOLTIP_OFFSET,
        },
        maxWidth: {
            type: Number,
            default: DEFAULT_MAX_WIDTH_REM,
        },
    },
    data() {
        return {
            isHovered: false,
            isFocusWithin: false,
            isPositioned: false,
            isVisible: false,
            resolvedSide: 'top',
            tooltipId: `tooltip-${++tooltipCounter}`,
            positionStyle: {
                left: '0px',
                top: '0px',
            },
            arrowStyle: {},
            cleanupAutoUpdate: null,
            describedElement: null,
        };
    },
    computed: {
        /** Vue 3 exposes kebab slot names on $slots as camelCase. */
        hasTooltipContentSlot() {
            return Boolean(
                this.$slots['tooltipContent'] || this.$slots['tooltip-content']
            );
        },
        floatingStyle() {
            return {
                ...this.positionStyle,
                '--tooltip-max-width': `${this.maxWidth}rem`,
            };
        },
    },
    methods: {
        handleMouseEnter(event) {
            this.isHovered = true;
            this.showTooltip(event);
        },
        handleMouseLeave() {
            this.isHovered = false;
            if (!this.isFocusWithin) this.hideTooltip();
        },
        handleFocusIn(event) {
            this.isFocusWithin = true;
            this.showTooltip(event);
        },
        handleFocusOut(event) {
            if (this.$refs.trigger?.contains(event.relatedTarget)) return;

            this.isFocusWithin = false;
            if (!this.isHovered) this.hideTooltip();
        },
        handleActivation() {
            this.isFocusWithin = false;
            this.hideTooltip();
        },
        handleEscape() {
            this.isHovered = false;
            this.isFocusWithin = false;
            this.hideTooltip();
        },
        showTooltip(event) {
            if (!this.$refs.trigger || !this.$refs.tooltip) return;

            this.isVisible = true;
            this.isPositioned = false;
            this.attachDescription(event);

            this.$nextTick(() => {
                if (!this.isVisible) return;
                this.startPositioning();
            });
        },
        hideTooltip() {
            this.isVisible = false;
            this.stopPositioning();
            this.detachDescription();
        },
        startPositioning() {
            const reference = this.getPositionReference();
            const tooltip = this.$refs.tooltip;

            if (!reference || !tooltip) return;

            this.stopPositioning();
            this.cleanupAutoUpdate = autoUpdate(reference, tooltip, () => {
                this.updatePosition(reference);
            });
        },
        stopPositioning() {
            if (!this.cleanupAutoUpdate) return;
            this.cleanupAutoUpdate();
            this.cleanupAutoUpdate = null;
        },
        async updatePosition(reference) {
            const tooltip = this.$refs.tooltip;
            const arrowElement = this.$refs.arrow;

            if (!this.isVisible || !tooltip || !arrowElement) return;

            const preferredSide = this.position === 'bottom' ? 'bottom' : 'top';
            const placement = this.placementFor(preferredSide);
            const middleware = [floatingOffset(this.offset)];

            if (this.position === 'auto') {
                middleware.push(
                    flip({
                        padding: VIEWPORT_PADDING,
                        fallbackPlacements: [this.placementFor('bottom')],
                    })
                );
            }

            middleware.push(
                shift({ padding: VIEWPORT_PADDING }),
                arrow({ element: arrowElement, padding: ARROW_PADDING })
            );

            const {
                x,
                y,
                placement: finalPlacement,
                middlewareData,
            } = await computePosition(reference, tooltip, {
                strategy: 'fixed',
                placement,
                middleware,
            });

            if (!this.isVisible) return;

            const side = finalPlacement.split('-')[0];
            const staticSide = {
                top: 'bottom',
                right: 'left',
                bottom: 'top',
                left: 'right',
            }[side];
            const arrowCoordinates = middlewareData.arrow || {};

            this.resolvedSide = side;
            this.positionStyle = {
                left: `${x}px`,
                top: `${y}px`,
            };
            this.arrowStyle = {
                left:
                    arrowCoordinates.x == null ? '' : `${arrowCoordinates.x}px`,
                top:
                    arrowCoordinates.y == null ? '' : `${arrowCoordinates.y}px`,
                right: '',
                bottom: '',
                [staticSide]: `${-ARROW_SIZE / 2}px`,
            };
            this.isPositioned = true;
        },
        placementFor(side) {
            return this.align === 'center' ? side : `${side}-${this.align}`;
        },
        getAnchorElement() {
            const anchors = this.$refs.trigger?.querySelectorAll(
                '[data-tooltip-anchor]'
            );

            if (anchors?.length) return anchors[anchors.length - 1];
            return this.$refs.trigger || null;
        },
        getPositionReference() {
            const anchorElement = this.getAnchorElement();

            if (!anchorElement) return null;
            if (anchorElement.dataset?.tooltipAnchor !== 'group') {
                return anchorElement;
            }

            return {
                contextElement: anchorElement,
                getBoundingClientRect: () =>
                    this.calculateGroupBounds(anchorElement),
            };
        },
        calculateGroupBounds(anchorElement) {
            const items = anchorElement.querySelectorAll(
                '[data-tooltip-anchor-item]'
            );
            let left = Infinity;
            let top = Infinity;
            let right = -Infinity;
            let bottom = -Infinity;

            items.forEach((item) => {
                const rect = item.getBoundingClientRect();
                if (rect.width === 0 && rect.height === 0) return;

                left = Math.min(left, rect.left);
                top = Math.min(top, rect.top);
                right = Math.max(right, rect.right);
                bottom = Math.max(bottom, rect.bottom);
            });

            if (left === Infinity) return anchorElement.getBoundingClientRect();

            return {
                x: left,
                y: top,
                left,
                top,
                right,
                bottom,
                width: right - left,
                height: bottom - top,
            };
        },
        findDescribedElement(event) {
            const eventTarget = event?.target;

            if (
                eventTarget &&
                eventTarget !== this.$refs.trigger &&
                eventTarget.matches?.(FOCUSABLE_SELECTOR)
            ) {
                return eventTarget;
            }

            const anchorElement = this.getAnchorElement();
            if (!anchorElement) return null;
            if (anchorElement.matches?.(FOCUSABLE_SELECTOR)) {
                return anchorElement;
            }

            return anchorElement.querySelector?.(FOCUSABLE_SELECTOR) || null;
        },
        attachDescription(event) {
            const describedElement = this.findDescribedElement(event);
            if (!describedElement) return;

            this.detachDescription();

            const descriptionIds = new Set(
                (describedElement.getAttribute('aria-describedby') || '')
                    .split(/\s+/)
                    .filter(Boolean)
            );
            descriptionIds.add(this.tooltipId);
            describedElement.setAttribute(
                'aria-describedby',
                [...descriptionIds].join(' ')
            );
            this.describedElement = describedElement;
        },
        detachDescription() {
            if (!this.describedElement) return;

            const descriptionIds = (
                this.describedElement.getAttribute('aria-describedby') || ''
            )
                .split(/\s+/)
                .filter((id) => id && id !== this.tooltipId);

            if (descriptionIds.length) {
                this.describedElement.setAttribute(
                    'aria-describedby',
                    descriptionIds.join(' ')
                );
            } else {
                this.describedElement.removeAttribute('aria-describedby');
            }

            this.describedElement = null;
        },
    },
    beforeUnmount() {
        this.stopPositioning();
        this.detachDescription();
    },
};
</script>

<style scoped>
.tooltip-trigger {
    position: relative;
    display: inline-block;
}

.tooltip-trigger--full {
    display: block;
    width: 100%;
}

.tooltip-text {
    position: fixed;
    z-index: var(--z-index-tooltip, 1055);
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    max-width: min(var(--tooltip-max-width, 18rem), calc(100vw - 1rem));
    padding: 0.375rem 0.75rem;
    border-radius: var(--radius-sm);
    background: var(--black);
    color: var(--white);
    font-family: Inter, sans-serif;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1rem;
    overflow-wrap: break-word;
    text-align: center;
    text-wrap: balance;
    pointer-events: none;
    visibility: hidden;
    opacity: 0;
    transform: scale(0.98);
    transform-origin: center bottom;
    transition: opacity 110ms ease-out, transform 110ms ease-out,
        visibility 0s linear 110ms;
}

.tooltip-text[data-side='bottom'] {
    transform-origin: center top;
}

.tooltip-text--visible {
    visibility: visible;
    opacity: 1;
    transform: scale(1);
    transition-delay: 0s;
}

.tooltip-arrow {
    position: absolute;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 0.0625rem;
    background: var(--black);
    transform: rotate(45deg);
}

@media (prefers-reduced-motion: reduce) {
    .tooltip-text,
    .tooltip-text--visible {
        transform: none;
        transition: none;
    }
}
</style>
